// Canvas 0: Case Card. Reference definition: the pattern every other canvas follows.
// Sections A to F, then the Module 1 notes. Sections B, C, D feed Canvases 1, 4, 7, 8 and 9.
import { makeDef, G, text, long, pick, tag, calc, fields, table, note } from './base.js';
import { OPT } from '../spine.js';
import { has, S, T, listOf, gap } from '../helpers.js';

const CAUSES = ['No clear business value', 'Nobody owns it across teams', 'Poor or scattered data', 'Risk controls arrive late'];
const D_ROWS = [
  { id: 'd1', label: 'A CX number', sub: 'satisfaction, effort or complaint resolution' },
  { id: 'd2', label: 'An efficiency number', sub: 'cost to serve or handling time' },
  { id: 'd3', label: 'A retention or growth number' },
];
const F_ROWS = [
  { id: 'bh', label: 'Business head' },
  { id: 'cfo', label: 'CFO' },
  { id: 'risk', label: 'Risk and Compliance' },
  { id: 'it', label: 'IT and Data' },
  { id: 'front', label: 'Frontline' },
];

const NORTHSTAR = {
  'a.function': 'Retail banking',
  'a.segment':
    'Retail customers in three ASEAN markets, the largest base outside Singapore: 2.4 million customers, 140 branches, a 400-seat contact centre, a mobile app used by 60% of customers',
  'a.sanitised': 'No',
  'b.journey': 'Account opening with a starter credit line',
  'b.break1': 'The form asks for what the bank already holds',
  'b.break2': 'A decline comes with no reason',
  'b.channels': 'Web, mobile app and branch',
  'b.volume': 'About 20,000 applications started a month',
  'b.volume_tag': 'E',
  'b.who':
    'Existing customers taking a second product, and new customers with no national digital identity record (none exists in two of the three markets)',
  'c.sponsor': 'Head of Retail Banking',
  'c.kpi1': 'Complaint resolution time',
  'c.kpi2': '12-month customer retention',
  'c.say_yes':
    'A shared plan with governance built in: the board will stop funding AI pilots that have no shared plan and no governance built in',
  ...T(
    'd',
    [
      { measure: 'Complaint resolution time', value: '9', unit: 'business days, average to final resolution', tag: 'M', source: 'Complaints report; the board standard is 3 business days' },
      { measure: 'Cost to serve', tag: 'G', source: 'Not yet known: find where it lives and who owns it' },
      { measure: '12-month customer retention', tag: 'G', source: 'Not yet known' },
    ],
    ['d1', 'd2', 'd3'],
  ),
  'e.idea': 'A churn model exists, but nobody in CX uses it',
  'e.stage': 'Pilot',
  'e.data': 'Churn model data; no owner named',
  ...T(
    'f',
    [
      { name: 'Head of Retail Banking', stance: 'Ally', note: 'The sponsor' },
      { note: 'Not yet engaged' },
      { name: 'Head of Risk', stance: 'Sceptic', note: 'Sees projects only after launch' },
      { name: 'Head of IT and Data', stance: 'Neutral' },
      { note: 'Not yet engaged' },
    ],
    ['bh', 'cfo', 'risk', 'it', 'front'],
  ),
  'm1.type': 'scores',
  'm1.rule': 'Every applicant is asked for every field, even where the bank already holds the answer',
  'm1.causes': 'Nobody owns it across teams,Risk controls arrive late',
};

export default makeDef(0, {
  short: 'Case Card',
  purpose: 'Pick the one real case you will carry through the whole course, and write down what you already know about it.',
  howTo: [
    'Work through sections A to F in order. The figures can be rough: tag each number M (measured), E (estimated) or G (guess).',
    'If you do not have a number, write where it lives and who owns it. That is your first action.',
    'Sections B, C and D feed the canvases that follow, so what you write here appears there automatically.',
  ],
  results: [
    { id: 'sections', label: 'Sections complete', rule: 'Count the sections A to F that are filled in.' },
    { id: 'tagged', label: 'Baseline numbers tagged', rule: 'Count the tags in section D.' },
    { id: 'gaps', label: 'Tagged E or G (go to Evidence Gaps)', rule: 'Count the E and the G in section D.' },
    { id: 'stances', label: 'People with a stance', rule: 'Count the stances in section F.' },
    { id: 'sceptics', label: 'Sceptics marked', rule: 'Count the Sceptics in section F.' },
  ],
  signoff: { mode: 'questions' },
  steps: [
    {
      letter: 'A',
      hint: 'Say where this case sits. Section A sets whether the whole pack uses sanitised figures.',
      blocks: [
        fields([
          text('a.function', 'Function', {
            required: true,
            w: 2,
            placeholder: 'For example: Retail banking',
            guide: G('The business function that owns the journey you will work on.', 'Retail banking'),
          }),
          pick('a.sanitised', 'Sanitised figures?', OPT.yesno, {
            required: true,
            w: 2,
            guide: G(
              'Choose Yes if you cannot share real figures in the room. Then enter ranges or indexed values (base 100) in place of real figures.',
              'No',
              undefined,
              'Every formula works the same on sanitised figures, so there is no need to leave a number blank.',
            ),
          }),
          long('a.segment', 'Customer segment and size', {
            required: true,
            w: 4,
            rows: 3,
            placeholder: 'Who the customers are, and roughly how many',
            guide: G(
              'Name the customers and give the scale in one or two lines.',
              'Retail customers in three ASEAN markets: 2.4 million customers, 140 branches, a 400-seat contact centre, a mobile app used by 60% of customers.',
            ),
          }),
        ]),
        note('Yes means: enter ranges or indexed values (base 100) in place of real figures.'),
      ],
    },
    {
      letter: 'B',
      hint: "Name the one journey you will carry, in the customer's words, and where it breaks today.",
      blocks: [
        fields([
          text('b.journey', "Priority journey, in the customer's words", {
            required: true,
            w: 4,
            placeholder: 'For example: Open an account',
            guide: G(
              'Pick one journey a customer would recognise, from the first step to the outcome they wanted.',
              'Account opening with a starter credit line',
              undefined,
              'A department or a system is not a journey. Name what the customer is trying to get done.',
            ),
          }),
          text('b.break1', 'Where it breaks today: moment 1', {
            required: true,
            w: 4,
            guide: G('The first moment where the customer waits, repeats themselves or gives up.', 'The form asks for what the bank already holds'),
          }),
          text('b.break2', 'Where it breaks today: moment 2 (if any)', {
            w: 4,
            guide: G('A second break, if there is one. Leave it blank if there is not.', 'A decline comes with no reason'),
          }),
          text('b.channels', 'Channels involved', {
            required: true,
            w: 2,
            placeholder: 'Web, app, branch, phone',
            guide: G('Every channel the customer can use on this journey.', 'Web, mobile app and branch'),
          }),
          text('b.volume', 'Volume on this journey (number, period)', {
            required: true,
            w: 2,
            placeholder: 'For example: about 20,000 a month',
            guide: G('How many customers go through it, and over what period. A rough number is fine.', 'About 20,000 applications started a month'),
          }),
          tag('b.volume_tag', 'Tag for the volume', {
            required: true,
            w: 1,
            guide: G(
              'M if it comes from a report you can show, E if you estimated it with a basis, G if it is a guess.',
              'E',
              'A number tagged E or G goes to the Evidence Gaps list.',
            ),
          }),
          long('b.who', 'Who feels the break most', {
            w: 4,
            rows: 3,
            guide: G(
              'The customers who suffer most when the journey breaks.',
              'Existing customers taking a second product, and new customers with no national digital identity record.',
            ),
          }),
        ]),
      ],
    },
    {
      letter: 'C',
      hint: 'Name the executive who would sponsor this, and the two KPIs they are measured on.',
      blocks: [
        fields([
          text('c.sponsor', 'Executive who would sponsor this (a role)', {
            required: true,
            w: 4,
            placeholder: 'A role, not a name',
            guide: G('The role that owns the journey and the budget.', 'Head of Retail Banking', undefined, 'Write a role. A person can change; the role stays.'),
          }),
          text('c.kpi1', 'KPI 1 they are measured on', {
            required: true,
            w: 2,
            guide: G('A measure their own performance review would name.', 'Complaint resolution time'),
          }),
          text('c.kpi2', 'KPI 2 they are measured on', {
            required: true,
            w: 2,
            guide: G('A second measure. It feeds the value case on Canvas 4.', '12-month customer retention'),
          }),
          long('c.say_yes', 'What would make them say yes', {
            w: 4,
            rows: 3,
            guide: G(
              'In their words: the condition under which this sponsor would back the work.',
              'A shared plan with governance built in.',
            ),
          }),
        ]),
      ],
    },
    {
      letter: 'D',
      hint: 'Write the three baseline numbers you can use as proof. No number? Write where it lives and who owns it: that is your first action.',
      blocks: [
        table('d', {
          rowLabel: 'Number',
          rows: { fixed: D_ROWS },
          pdf: 'cards',
          columns: [
            text('measure', 'Measure', {
              required: true,
              w: 2,
              guide: G('Name the measure.', 'Complaint resolution time', undefined, 'Name a measure, not a goal. "Improve CX" is a goal.'),
            }),
            text('value', 'Value', {
              w: 1,
              guide: G('The figure, if you have one. Leave it blank if you do not.', '9'),
            }),
            text('unit', 'Unit', {
              w: 1,
              guide: G('What the number counts, and over what.', 'business days, average to final resolution'),
            }),
            tag('tag', 'Tag', {
              required: true,
              w: 1,
              guide: G(
                'M for a number from a report or system you can show; E for an estimate with a stated basis; G for a guess.',
                'M for complaint resolution; G for cost to serve (not yet known).',
                'A number tagged E or G goes to the Evidence Gaps list.',
                'No number yet? Tag it G and write where it lives. Do not invent one.',
              ),
            }),
            text('source', 'Source, or where it lives and who owns it', {
              required: true,
              w: 4,
              guide: G(
                'Where the number comes from, or where it lives and who owns it if you do not have it yet.',
                'Complaints report; the board standard is 3 business days',
              ),
            }),
          ],
        }),
        note('No number? Write where it lives and who owns it. That is your first action.', 'rule'),
      ],
    },
    {
      letter: 'E',
      hint: 'Name the AI idea already in the air in your organisation, so Canvas 2 can include it as a candidate.',
      blocks: [
        fields([
          long('e.idea', 'What has already been proposed', {
            required: true,
            w: 4,
            rows: 2,
            guide: G('The AI idea someone has already raised. If there is none, write "None yet".', 'A churn model exists, but nobody in CX uses it'),
          }),
          pick('e.stage', 'Stage (idea, pilot or live)', OPT.stage, {
            required: ({ v }) => has(v['e.idea']),
            w: 2,
            guide: G('Idea: only talked about. Pilot: being tried. Live: in production.', 'Pilot'),
          }),
          long('e.data', 'Data it would need, and where it sits', {
            w: 4,
            rows: 2,
            guide: G('The data the idea would use and where it lives today.', 'Churn model data; no owner named'),
          }),
        ]),
        note('Mark this idea "Yes" among the candidates on Canvas 2.'),
      ],
    },
    {
      letter: 'F',
      hint: 'Note where each person stands. A blank is a finding: it shows who you have not yet spoken to.',
      blocks: [
        table('f', {
          rowLabel: 'Role',
          rows: { fixed: F_ROWS },
          pdf: 'table',
          columns: [
            text('name', 'Name or role title', { w: 2, guide: G('A role title is enough.', 'Head of Retail Banking') }),
            pick('stance', 'Stance', OPT.stance, {
              w: 1,
              guide: G('Ally backs it, Neutral has not decided, Sceptic doubts it. Leave blank if you have not asked.', 'Ally', undefined, 'A blank is a finding, not a gap to fill with a guess.'),
            }),
            text('note', 'Note (a blank is a finding)', { w: 3, guide: G('One line on why, or "Not yet engaged".', 'Sees projects only after launch') }),
          ],
        }),
      ],
    },
    {
      id: 'notes',
      letter: 'M',
      title: 'Module 1 notes',
      hint: 'Fill these in the room, during Module 1.',
      blocks: [
        fields([
          pick('m1.type', 'My idea scores, writes or acts', OPT.aitype, {
            w: 2,
            guide: G('Scores ranks, flags or forecasts. Writes creates new content on request. Acts plans its own steps, then takes them.', 'scores'),
          }),
          long('m1.rule', 'One rule that breaks on my journey', {
            w: 4,
            rows: 2,
            guide: G('A rule or step that makes sense on paper and fails the customer.', 'Every applicant is asked for every field, even where the bank already holds the answer.'),
          }),
          { id: 'm1.causes', label: 'Causes I have already seen', kind: 'multi', options: CAUSES, w: 4, guide: G('Tick every cause you have already seen on your journey.') },
        ]),
      ],
    },
  ],
  derive(v) {
    const calcOut = {};
    const sections = [];
    sections.push(has(v['a.function']) && has(v['a.segment']) && has(v['a.sanitised']));
    sections.push(has(v['b.journey']) && has(v['b.break1']) && has(v['b.channels']));
    sections.push(has(v['c.sponsor']) && has(v['c.kpi1']) && has(v['c.kpi2']));
    const dRows = D_ROWS.map((r) => ({
      id: r.id,
      measure: S(v, `d.${r.id}.measure`),
      value: S(v, `d.${r.id}.value`),
      unit: S(v, `d.${r.id}.unit`),
      tag: S(v, `d.${r.id}.tag`),
      source: S(v, `d.${r.id}.source`),
    }));
    sections.push(dRows.every((r) => r.measure && (r.value || r.source)));
    sections.push(has(v['e.idea']) && has(v['e.stage']));
    const people = F_ROWS.map((r) => ({
      id: r.id,
      role: r.label,
      name: S(v, `f.${r.id}.name`),
      stance: S(v, `f.${r.id}.stance`),
      note: S(v, `f.${r.id}.note`),
    }));
    sections.push(people.every((p) => p.stance));
    const done = sections.filter(Boolean).length;
    const tagged = dRows.filter((r) => r.tag).length;
    const eg = dRows.filter((r) => r.tag === 'E' || r.tag === 'G').length;
    const stances = people.filter((p) => p.stance).length;
    const sceptics = people.filter((p) => p.stance === 'Sceptic').length;
    const any = Object.keys(v).some((k) => !k.startsWith('_') && has(v[k]));
    const results = any
      ? {
          sections: `${done} of 6`,
          tagged: tagged ? `${tagged} of 3` : '',
          gaps: tagged ? String(eg) : '',
          stances: stances ? `${stances} of 5` : '',
          sceptics: stances ? String(sceptics) : '',
        }
      : {};
    const evidence = [];
    if (S(v, 'b.volume_tag') === 'E' || S(v, 'b.volume_tag') === 'G') {
      evidence.push(gap(`Volume: ${S(v, 'b.volume') || 'journey volume'}`, S(v, 'b.volume_tag'), 'Case Card B'));
    }
    dRows.forEach((r, i) => {
      if (r.tag === 'E' || r.tag === 'G') evidence.push(gap(`Baseline: ${r.measure}`, r.tag, `Case Card D, row ${i + 1}`));
    });
    const warnings = [];
    dRows.forEach((r, i) => {
      if (r.measure && !r.value && !r.source) warnings.push({ step: 's4', text: `Row ${i + 1}: write the value, or where it lives and who owns it.` });
    });
    return {
      calc: calcOut,
      results,
      evidence,
      warnings,
      facts: {
        function: S(v, 'a.function'),
        segment: S(v, 'a.segment'),
        sanitised: S(v, 'a.sanitised'),
        journey: S(v, 'b.journey'),
        break1: S(v, 'b.break1'),
        break2: S(v, 'b.break2'),
        channels: S(v, 'b.channels'),
        volume: S(v, 'b.volume'),
        volumeTag: S(v, 'b.volume_tag'),
        who: S(v, 'b.who'),
        sponsor: S(v, 'c.sponsor'),
        kpi1: S(v, 'c.kpi1'),
        kpi2: S(v, 'c.kpi2'),
        sayYes: S(v, 'c.say_yes'),
        baselines: dRows,
        idea: S(v, 'e.idea'),
        ideaStage: S(v, 'e.stage'),
        ideaData: S(v, 'e.data'),
        people,
        ideaType: S(v, 'm1.type'),
        rule: S(v, 'm1.rule'),
        causes: listOf(S(v, 'm1.causes')),
      },
    };
  },
  NORTHSTAR,
});
